import { RefObject, useEffect, useRef } from 'react';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [contenteditable="true"]';

const trapStack: symbol[] = [];

const isRendered = (element: HTMLElement, container: HTMLElement) => {
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    const { display, visibility } = getComputedStyle(node);
    if (display === 'none' || (node === element && visibility === 'hidden')) return false;
    if (node === container) break;
  }
  return true;
};

const getTabbables = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(element =>
    isRendered(element, container),
  );

export const useFocusTrap = (
  ref: RefObject<HTMLElement | null>,
  { isActive = true }: { isActive?: boolean } = {},
) => {
  const lastOutsideFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    lastOutsideFocusRef.current = document.activeElement;
    const track = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Element && !ref.current?.contains(target)) {
        lastOutsideFocusRef.current = target;
      }
    };
    document.addEventListener('focusin', track);
    return () => document.removeEventListener('focusin', track);
  }, [ref]);

  useEffect(() => {
    const container = ref.current;
    if (!isActive || !container) return;

    const token = Symbol('focusTrap');
    trapStack.push(token);
    const active = document.activeElement;
    const trigger = active && container.contains(active) ? lastOutsideFocusRef.current : active;

    if (!container.contains(document.activeElement)) {
      container.focus({ preventScroll: true });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || trapStack[trapStack.length - 1] !== token) return;
      const tabbables = getTabbables(container);
      if (tabbables.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      trapStack.splice(trapStack.indexOf(token), 1);
      const current = document.activeElement;
      const hasLostFocus =
        !current || current === document.body || container.contains(current);
      if (
        hasLostFocus &&
        trigger instanceof HTMLElement &&
        trigger !== document.body &&
        trigger.isConnected &&
        !trigger.matches(TEXT_ENTRY_SELECTOR)
      ) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [ref, isActive]);
};
